import { lazy, Suspense, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { transformExtent } from "ol/proj";
import Row from "@avinet/adaptive-ui-core/layout/Row";
import { useTranslate } from "@avinet/adaptive-ui-translate";
import {
  GeometrySection,
  GeometrySectionType,
  InputSection,
  InputSectionType,
} from "../../types/Section";
import Survey from "../../types/Survey";
import { Icon } from "../Icon";
import Loading, { LoadingOverlay } from "../Loading";
import DefaultMap from "../map/DefaultMap";
import GeometryResults from "./GeometryResults";
import ResultsDownload from "./ResultsDownload";
import "./Results.scss";

const ResultsTable = lazy(() => import("./ResultsTable"));
const ResultsSection = lazy(() => import("./ResultsSection"));

export type ResultsFilter = {
  values: any[];
  stringValues: string[];
  filter: (record: any) => boolean;
};
export type ResultsFilters = { [id: string]: ResultsFilter | undefined };

export function useFilteredData(
  id: string | null,
  filters: ResultsFilters,
  data: any[]
) {
  return useMemo(() => {
    let filteredData = data;
    Object.keys(filters).forEach((filterId) => {
      const filter = filters[filterId];
      if (filterId === id || !filter) return;
      filteredData = filteredData.filter((record: any) =>
        filter.filter(record)
      );
    });
    return filteredData;
  }, [id, data, filters]);
}

const Results = ({ survey, data }: { survey: Survey; data: any[] }) => {
  const t = useTranslate("Results");

  const [download, setDownload] = useState(false);
  const [filters, setFilters] = useState<ResultsFilters>({});

  const handleSetFilter = useCallback((id: string, filter?: ResultsFilter) => {
    setFilters((filters) => ({ ...filters, [id]: filter }));
  }, []);

  const geometrySections = useMemo(
    () =>
      survey.schema.filter(
        (section) => section.type in GeometrySectionType
      ) as GeometrySection[],
    [survey]
  );

  const inputSections = useMemo(
    () =>
      survey.schema.filter(
        (section) => section.type in InputSectionType
      ) as InputSection[],
    [survey]
  );

  const layers = useMemo(() => {
    if (geometrySections.length === 0) return undefined;
    const section = geometrySections[0];
    if (!section.basemap) return undefined;
    return [
      {
        id: "basemap",
        sourceType: "adaptive",
        themeType: "basetheme",
        themeUuid: section.basemap,
      },
    ];
  }, [geometrySections]);

  const transformedExtent = useMemo(() => {
    if (geometrySections.length === 0) return undefined;
    const section = geometrySections[0];
    if (!section.extent) return;

    return transformExtent(section.extent, "EPSG:4326", "EPSG:3857");
  }, [geometrySections]);

  const filterDisplay = useMemo(() => {
    return Object.keys(filters)
      .filter((id) => !!filters[id])
      .map((id) => ({
        id,
        label: inputSections.find((s) => s.columnName === id)?.label,
        values: filters[id]!.stringValues,
      }));
  }, [inputSections, filters]);

  const [printing, setPrinting] = useState(false);
  const print = useCallback(() => {
    document.body.classList.add("print");
    setPrinting(true);
    setTimeout(() => {
      window.print();
      document.body.classList.remove("print");
      setPrinting(false);
    }, 1000);
  }, []);

  return (
    <div className="results">
      <Row className="results-toolbar">
        <Link className="btn-link" to="/survey">
          <Icon name="chevron" rotate={180} /> {t("backToAllSurveys")}
        </Link>
        <button type="button" className="btn" onClick={print}>
          <Icon name="documentPdf" /> {t("print")}
        </button>
        <button type="button" className="btn" onClick={() => setDownload(true)}>
          <Icon name="arrowDownload" /> {t("download")}
        </button>
      </Row>

      <h1>{survey.metaTitle}</h1>
      <h2 className="print-warning">{t("printWarning")}</h2>
      <h2>{t("responses", { n: data.length })}</h2>
      {!!filterDisplay.length && (
        <div className="results-filters">
          <span className="tag">{t("filters")}</span>
          {filterDisplay.map((f) => (
            <span key={f.id}>
              <b>{f.label}</b>:{" "}
              {f.values
                .map((f) => (f === undefined ? t("noAnswer") : f))
                .join(", ")}
            </span>
          ))}{" "}
          <button
            type="button"
            className="btn-link"
            onClick={() => setFilters({})}
          >
            <Icon name="dismiss" /> {t("clearAllFilters")}
          </button>
        </div>
      )}

      <div className="results-grid">
        {geometrySections.length > 0 && (
          <DefaultMap layers={layers} initialExtent={transformedExtent}>
            <div className="results-geometries">
              {geometrySections.map((section) => (
                <GeometryResults
                  key={section.columnName}
                  section={section}
                  data={data}
                  filters={filters}
                  setFilter={handleSetFilter}
                />
              ))}
            </div>
          </DefaultMap>
        )}

        <p className="info">{t("resultsInfo")}</p>

        <Suspense fallback={<Loading />}>
          {inputSections
            .filter((section) => !section.administrative)
            .map((section) => (
              <ResultsSection
                key={section.columnName}
                section={section}
                data={data}
                filters={filters}
                setFilter={handleSetFilter}
              />
            ))}
        </Suspense>
      </div>

      <Suspense fallback={<Loading />}>
        <ResultsTable
          uuid={survey.uuid!}
          data={data}
          inputSections={inputSections}
          filters={filters}
        />
      </Suspense>

      {printing && <LoadingOverlay />}

      {download && (
        <ResultsDownload survey={survey} onClose={() => setDownload(false)} />
      )}
    </div>
  );
};

export default Results;
